import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type SitesBulkResponse } from './types';
import { zodSitesBulkPayloadSchema } from './validations';

/**
 * Fetches multiple sites in bulk from the API.
 * @param {UUID[]} siteIds - Array of site IDs to fetch.
 * @returns {Promise<Site[]>} Array of site data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/query_sites_in_bulk_site_query_bulk_post
 */
export const fetchSitesBulkAdmin = async (
  siteIds: UUID[],
  options?: KyRequestOptions,
) => {
  if (!siteIds?.length) {
    return [];
  }

  try {
    const response = await vestigasApi
      .post(ENDPOINT.SITE.GET_BULK_ADMIN(), {
        json: { ids: siteIds },
        validate: { body: zodSitesBulkPayloadSchema },
        ...options,
      })
      .json<SitesBulkResponse>();

    return response?.items ?? [];
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Sites Bulk Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.site.getBulk.error422, {
            ns: 'data',
          });
          break;
        }

        case 429: {
          errorMessage = i18n.t(($) => $.site.getBulk.error429, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.site.getBulk.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
